import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Flame, Sparkles, Trophy, Crown, Gift, Shield } from "lucide-react";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GAMES } from "@/lib/games";
import { useRace } from "@/lib/useRace";
import GameCard from "@/components/GameCard";
import LiveFeed from "@/components/LiveFeed";

const SLIDES = [
  { key: "signup", eyebrow: "Welcome", title: "1,000 FMI on signup", text: "Create an account or connect MetaMask and the vault pays you first.", cta: "Claim bonus", to: null, auth: true, icon: Gift, accent: "#FFC107",
    image: "https://images.pexels.com/photos/32624440/pexels-photo-32624440.jpeg?auto=compress&cs=tinysrgb&w=1400" },
  { key: "deposit", eyebrow: "First deposit", title: "100% match bonus", text: "Deposit BTC, ETH, USDT or SOL and we match it in FMI — up to 5,000 FMI, credited instantly.", cta: "Deposit now", to: "/deposit", auth: true, icon: Sparkles, accent: "#00F0FF",
    image: "https://images.unsplash.com/photo-1672911640817-d2902754be5a?w=1400&q=80" },
  { key: "race", eyebrow: "Weekly race", title: (race) => `${formatAmount(race?.pool ?? 10000, "FMI")} FMI prize pool`, text: "Top 10 wagerers split the pool every Monday. Every FMI bet counts.", cta: "View race", to: "/leaderboard", icon: Trophy, accent: "#00E676",
    image: "https://images.pexels.com/photos/31650385/pexels-photo-31650385.jpeg?auto=compress&cs=tinysrgb&w=1400" },
  { key: "vip", eyebrow: "VIP Club", title: "Up to 25% rakeback", text: "Five tiers from Bronze to Diamond. Weekly rakeback on everything you wager.", cta: "See tiers", to: "/vip", icon: Crown, accent: "#FF3366",
    image: "https://images.pexels.com/photos/10053361/pexels-photo-10053361.jpeg?auto=compress&cs=tinysrgb&w=1400" },
];

const CATS = [
  { key: "lobby", label: "Lobby" },
  { key: "originals", label: "Originals" },
  { key: "hot", label: "Hot" },
  { key: "new", label: "New" },
  { key: "slots", label: "Slots" },
];

function Hero({ onAuth }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const race = useRace();
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % SLIDES.length), 6000); return () => clearInterval(t); }, []);
  const s = SLIDES[i];
  const title = typeof s.title === "function" ? s.title(race) : s.title;
  const Icon = s.icon;
  const go = () => (s.to ? nav(s.to) : user ? nav("/games/crash") : onAuth());
  return (
    <section className="relative rounded-2xl overflow-hidden border border-white/[0.08] h-[300px] md:h-[340px]" data-testid="hero-carousel">
      {SLIDES.map((sl, idx) => (
        <img key={sl.key} src={sl.image} alt="" className="absolute inset-0 w-full h-full object-cover hero-img" style={{ opacity: idx === i ? 1 : 0 }} />
      ))}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.7) 45%, rgba(10,10,10,0.15) 100%)" }} />
      <div className="absolute inset-0" style={{ background: `radial-gradient(500px 220px at 15% 100%, ${s.accent}26, transparent 70%)` }} />
      <div key={s.key} className="relative h-full flex flex-col justify-center px-6 md:px-12 max-w-2xl fade-up">
        <div className="chip w-fit mb-4" style={{ color: s.accent, borderColor: `${s.accent}66` }} data-testid="hero-badge"><Icon size={12} /> {s.eyebrow}</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95]" data-testid="hero-title">{title}</h1>
        <p className="text-zinc-300 mt-4 text-sm md:text-base max-w-md leading-relaxed">{s.text}</p>
        <div className="flex items-center gap-3 mt-6">
          <button onClick={go} className="btn-gold rounded-xl px-6 py-3 flex items-center gap-2 font-bold" data-testid="hero-cta-primary">{s.cta} <ArrowRight size={16} /></button>
          <button onClick={() => nav("/provably-fair")} className="btn-ghost rounded-xl px-5 py-3 hidden sm:flex items-center gap-2 text-sm" data-testid="hero-cta-secondary"><Shield size={15} /> Provably fair</button>
        </div>
      </div>
      <div className="absolute bottom-4 left-6 md:left-12 flex items-center gap-2" data-testid="hero-dots">
        {SLIDES.map((sl, idx) => (
          <button key={sl.key} onClick={() => setI(idx)} className="h-1.5 rounded-full" data-testid={`hero-dot-${idx}`}
            style={{ width: idx === i ? 28 : 8, background: idx === i ? s.accent : "rgba(255,255,255,0.25)", transition: "width 300ms ease, background-color 300ms ease" }} />
        ))}
      </div>
      <div className="absolute bottom-4 right-4 hidden sm:flex gap-2">
        <button onClick={() => setI((i - 1 + SLIDES.length) % SLIDES.length)} className="btn-ghost rounded-full w-9 h-9 flex items-center justify-center" data-testid="hero-prev"><ChevronLeft size={16} /></button>
        <button onClick={() => setI((i + 1) % SLIDES.length)} className="btn-ghost rounded-full w-9 h-9 flex items-center justify-center" data-testid="hero-next"><ChevronRight size={16} /></button>
      </div>
    </section>
  );
}

function Row({ title, icon: Icon, games, playing, testid }) {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 480, behavior: "smooth" });
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-base md:text-lg font-black tracking-tight flex items-center gap-2"><Icon size={18} className="text-amber-400" /> {title}</h2>
        <div className="flex gap-1.5">
          <button onClick={() => scroll(-1)} className="btn-ghost rounded-lg w-8 h-8 flex items-center justify-center" data-testid={`${testid}-prev`}><ChevronLeft size={14} /></button>
          <button onClick={() => scroll(1)} className="btn-ghost rounded-lg w-8 h-8 flex items-center justify-center" data-testid={`${testid}-next`}><ChevronRight size={14} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar snap-x pb-1">
        {games.map((g, idx) => (
          <GameCard key={g.slug} game={g} playing={playing[g.slug] || 0} className="w-[160px] md:w-[196px] shrink-0 snap-start stagger" style={{ animationDelay: `${idx * 60}ms` }} />
        ))}
      </div>
    </section>
  );
}

export default function Lobby() {
  const { openAuth } = useOutletContext() || {};
  const [cat, setCat] = useState("lobby");
  const [wins, setWins] = useState([]);
  const [playing, setPlaying] = useState({});

  useEffect(() => {
    api.get("/bets/big-wins?limit=6").then(({ data }) => setWins(data)).catch(() => {});
    const load = () => api.get("/bets/live?limit=100").then(({ data }) => {
      const since = Date.now() - 3600_000;
      const users = {};
      data.forEach((b) => { if (new Date(b.created_at) >= since) (users[b.game] ||= new Set()).add(b.user_id || b.username); });
      setPlaying(Object.fromEntries(Object.entries(users).map(([k, v]) => [k, v.size])));
    }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => (cat === "lobby" ? GAMES : GAMES.filter((g) => g.tags.includes(cat))), [cat]);

  return (
    <div className="flex flex-col gap-8 fade-up" data-testid="lobby">
      <Hero onAuth={openAuth} />

      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="category-chips">
        {CATS.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`cat-${c.key}`}
            className={`cat-chip ${cat === c.key ? "active" : ""}`}>{c.label}</button>
        ))}
      </div>

      {cat === "lobby" ? (
        <>
          <Row title="FMI Originals" icon={Sparkles} games={GAMES} playing={playing} testid="row-originals" />
          <Row title="Hot right now" icon={Flame} games={GAMES.filter((g) => g.tags.includes("hot"))} playing={playing} testid="row-hot" />
        </>
      ) : (
        <section data-testid="game-grid">
          <h2 className="font-display text-base md:text-lg font-black tracking-tight mb-3 capitalize">{CATS.find((c) => c.key === cat)?.label} <span className="text-zinc-500 text-sm font-normal ml-1">{filtered.length} games</span></h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {filtered.map((g) => <GameCard key={g.slug} game={g} playing={playing[g.slug] || 0} />)}
          </div>
        </section>
      )}

      {wins.length > 0 && (
        <section data-testid="big-wins">
          <h2 className="font-display text-base md:text-lg font-black tracking-tight mb-3 flex items-center gap-2"><Trophy size={18} className="text-amber-400" /> Biggest wins</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {wins.map((w) => (
              <div key={w.id} className="panel panel-hover p-4" data-testid={`big-win-${w.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{w.game}</div>
                <div className="font-mono-num text-emerald-400 text-lg font-bold mt-1">+{formatAmount(w.profit, w.currency)}</div>
                <div className="text-[11px] text-zinc-500 mt-1 truncate">{w.username}</div>
                <div className="text-[10px] text-zinc-600 font-mono-num mt-1">×{Number(w.multiplier).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <LiveFeed />
    </div>
  );
}
